import { useTranslation } from 'react-i18next'
import EditLayout from 'layouts/edit-layout/EditLayout'
import styles from './SwitchToFix.module.scss'
import { Banner, Button, Faq, Header, Modal, popSuccessToast } from '@boltenergy-be/design-system'
import Link from 'components/link/Link.tsx'
import { useState } from 'react'
import {
  CUSTOMER_TYPES,
  ENERGY_TYPES,
  getProductFromPriceListUrl,
  PRODUCT_TYPES,
  PRODUCTS,
  PRODUCTS_CONFIG,
  ServiceContractElectricity,
  ServiceContractGas
} from '@boltenergy-be/shared'
import backendLib from 'lib/bolt-backend'
import {
  selectCurrentAccount,
  selectCurrentBillingContract,
  selectIsProducerAccount,
  updateSelectedServiceContracts
} from 'store/contact/slice.ts'
import dayjs from 'dayjs'
import { ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import { boltApi } from 'store/queries/bolt-api'
import { TagTypes } from 'store/queries/bolt-api/types.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { SmartMeterOptions } from 'pages/app/addresses/offtake-address/types.ts'
import { ProductSwitchInputs } from 'pages/app/tariffs/products/components/product-switch-modal/types.ts'
import ProductDrawer from 'features/contracts/add/components/product-details-drawer/ProductDrawer.tsx'
import { useGetFaqBySlugQuery, useGetProductContentQuery } from 'store/queries/cms-api'
import { FaqSlugB2C } from 'types/faq.ts'
import { AccordionItem } from '@boltenergy-be/design-system'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import parse from 'html-react-parser'
import { selectPriceLists } from 'store/contact/selectors.ts'
import { Navigate } from 'react-router'
import { routes } from 'types/routes.ts'
import { isActiveContract } from 'utils/contracts.ts'
import { trackEvent } from 'utils/tracking.ts'

const SwitchToFix = () => {
  // Redux
  const { userType } = useStoreSelector((state) => state.auth)
  const account = useStoreSelector(selectCurrentAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const priceLists = useStoreSelector(selectPriceLists)
  const dispatch = useStoreDispatch()
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const contractActive = isActiveContract(billingContract.serviceContracts.electricity)

  // i18n
  const { t } = useTranslation(['boltFixOptIn', 'common', 'errors'])
  const language = useGetLanguage()

  // Local state
  const [actionState, setActionState] = useState<'sending' | 'error' | 'success' | null>(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showProductDetails, setShowProductDetails] = useState<boolean>(false)

  // Redux queries
  const { data: productsContent } = useGetProductContentQuery({ language })
  const { data: faq, isLoading } = useGetFaqBySlugQuery({
    slug: FaqSlugB2C.BOLT_FIX_OPT_IN,
    language
  })

  // Skip computations and early return with redirect if the user is not eligible for the fix product switch (producer, not in Flanders, or no active contract)
  if (isProducer || !contractActive) {
    return <Navigate to={routes.OVERVIEW} replace />
  }

  // Constants
  const currentElectricityPriceList = priceLists.electricity?.current
  const latestFuturePriceList = priceLists?.electricity?.future?.find((list) => !list.toDate)
  const priceListToUse = latestFuturePriceList || currentElectricityPriceList
  const mostRecentProduct = priceListToUse?.url ? getProductFromPriceListUrl(priceListToUse.url) : undefined

  if (!mostRecentProduct) return null

  const productConfig = PRODUCTS_CONFIG[mostRecentProduct]
  const productIsFix = productConfig.type === PRODUCT_TYPES.fix
  const allowedStartDate = latestFuturePriceList?.fromDate
    ? dayjs(latestFuturePriceList?.fromDate).add(1, 'day').format(ISO8601_DATE_FORMAT)
    : priceLists?.electricity?.current?.toDate || dayjs().add(1, 'day').format(ISO8601_DATE_FORMAT)
  const firstOfNextMonth = dayjs().add(1, 'month').startOf('month').format(ISO8601_DATE_FORMAT)
  const startDate = latestFuturePriceList?.fromDate ? allowedStartDate : firstOfNextMonth
  const newProduct = productConfig.isPlentyProduct ? PRODUCTS.plentyFix : PRODUCTS.fix
  const displayName = PRODUCTS_CONFIG[newProduct].name[language].toLowerCase().startsWith('plenty')
    ? PRODUCTS_CONFIG[newProduct].name[language]
    : `Bolt ${PRODUCTS_CONFIG[newProduct].name[language]}`
  const customerType = account?.company ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential

  // Return back to homepage if already on fixed product
  if (productIsFix) {
    return <Navigate to={routes.OVERVIEW} replace />
  }

  /**
   * Handles the confirmation of the instalment change
   */
  const switchProduct = async () => {
    setActionState('sending')

    const data: ProductSwitchInputs = {
      startDate: startDate,
      instalmentType: 'unchanged',
      smartMeterOptions: []
    }
    const eligibility = {
      [ENERGY_TYPES.electricity]: {
        [SmartMeterOptions.MONTHLY_BILLING]: false,
        [SmartMeterOptions.DYNAMIC_TARIFF]: false
      },
      [ENERGY_TYPES.gas]: {
        [SmartMeterOptions.MONTHLY_BILLING]: false,
        [SmartMeterOptions.DYNAMIC_TARIFF]: false
      }
    }

    try {
      const res = await backendLib.switchProduct({
        billingContract,
        eligibility,
        inputs: data,
        product: newProduct,
        userType
      })

      if ('success' in res && !res.success) {
        throw new Error(res.errorCode || 'Product switch failed')
      }

      if (ENERGY_TYPES.electricity in res) {
        popSuccessToast(
          t(($) => $.modal.confirmationMessage, {
            product: displayName
          })
        )

        trackEvent({
          category: 'product_switch',
          action: 'finish'
        })

        dispatch(
          updateSelectedServiceContracts({
            electricity: res[ENERGY_TYPES.electricity] as ServiceContractElectricity,
            gas: res[ENERGY_TYPES.gas] as ServiceContractGas | undefined
          })
        )
        setShowConfirmationModal(false)
        setActionState('success')
      }
      dispatch(boltApi.util.invalidateTags([TagTypes.EVENT_LOGS]))
    } catch (err) {
      setActionState('error')
      trackEvent({ category: 'product_switch', action: 'fail' })
      log({
        error: err instanceof Error ? err : 'Unknown error in switchProduct',
        identifier: `[${Flow.TARIFFS}:ProductSwitchModal:submit]`,
        metaData: {
          errorPayload: {
            err,
            billingContractId: billingContract.contractNumber,
            inputs: data,
            eligibility
          }
        }
      })
    }
  }

  return (
    <EditLayout title={t(($) => $.pageTitle)} classes={{ body: styles.body, header: styles['page-header'] }}>
      <Header
        title={t(($) => $.header.title)}
        color="light"
        fluid
        docked={false}
        titleVariant="h2"
        classnames={{ container: styles.header, content: styles['header-content'] }}
      >
        <p>{parse(t(($) => $.header.description))}</p>

        <div>
          <strong className="weight-semibold">{t(($) => $.header.info.subtitle)}</strong>
          <p>{t(($) => $.header.info.description)}</p>
        </div>

        <div className={styles['action-wrapper']}>
          <Button
            type="button"
            isFullwidthMobile
            onClick={() => {
              setShowConfirmationModal(true)
              trackEvent({
                category: 'product_switch',
                object: 'confirmation',
                action: 'view'
              })
            }}
          >
            {t(($) => $.header.action)}
          </Button>
          <Button
            type="button"
            isFullwidthMobile
            variant="secondary"
            onClick={() => {
              setShowProductDetails(true)
              trackEvent({
                category: 'product_switch',
                object: 'product_details',
                action: 'view'
              })
            }}
          >
            {t(($) => $.header.showDetails)}
          </Button>
        </div>
      </Header>

      {!isLoading && !!faq && (
        <Faq
          fluid
          title={t(($) => $.faqTitle)}
          articles={
            faq.category?.questions.map(
              (item) =>
                ({
                  title: item.question,
                  children: item.content ? (
                    <BlocksRenderer
                      content={item.content}
                      blocks={{
                        link: ({ children, url }) => (
                          <Link
                            as="a"
                            variant="inline"
                            href={url}
                            target={url.startsWith('http') && !url.includes('my.boltenergie') ? '_blank' : undefined}
                          >
                            {children}
                          </Link>
                        )
                      }}
                    />
                  ) : (
                    parse(item?.answer || '')
                  )
                }) satisfies AccordionItem
            ) || []
          }
        />
      )}

      <Modal
        isOpen={showConfirmationModal}
        onRequestClose={() => setShowConfirmationModal(false)}
        title={t(($) => $.modal.title)}
        titleVariant="h4"
      >
        <p>{t(($) => $.modal.description)}</p>

        <footer>
          <Button type="button" onClick={switchProduct} loading={actionState === 'sending'}>
            {t(($) => $.modal.confirm)}
          </Button>

          <Button type="button" variant="secondary" onClick={() => setShowConfirmationModal(false)} disabled={actionState === 'sending'}>
            {t(($) => $.cancel, {
              ns: 'common'
            })}
          </Button>
        </footer>

        {actionState === 'error' && (
          <Banner
            type="blocking"
            title={t(($) => $.default, {
              ns: 'errors'
            })}
          />
        )}
      </Modal>

      <ProductDrawer
        product={newProduct}
        productName={productsContent?.[newProduct]?.name}
        userType={customerType}
        isOpen={showProductDetails}
        onRequestClose={() => setShowProductDetails(false)}
        conditions={productsContent?.[newProduct]?.conditions || undefined}
        needsGas={!!billingContract?.serviceContracts?.gas}
      />
    </EditLayout>
  )
}

export default SwitchToFix
